import { computed, onBeforeUnmount, ref, watch, type Ref } from 'vue';

type UseImageRetryOptions = {
  maxRetries?: number;
  baseDelayMs?: number;
  cacheBusterKey?: string;
};

export const useImageRetry = (
  imageUrl: Ref<string>,
  options: UseImageRetryOptions = {}
) => {
  const {
    maxRetries = 4,
    baseDelayMs = 400,
    cacheBusterKey = 'v'
  } = options;

  const imgSrc = ref('');
  const retryCount = ref(0);
  let retryTimer: ReturnType<typeof setTimeout> | null = null;

  const clearRetryTimer = () => {
    if (retryTimer) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
  };

  const addCacheBuster = (url: string, token: string | number) => {
    if (!url) {
      return url;
    }
    const hasQuery = url.includes('?');
    return `${url}${hasQuery ? '&' : '?'}${cacheBusterKey}=${encodeURIComponent(String(token))}`;
  };

  const setFreshSrc = (url: string) => {
    // new token each time to defeat cached 404/403
    imgSrc.value = addCacheBuster(url, Date.now());
  };

  watch(
    imageUrl,
    (url) => {
      retryCount.value = 0;
      clearRetryTimer();

      if (url) {
        setFreshSrc(url);
      } else {
        imgSrc.value = '';
      }
    },
    { immediate: true }
  );

  const onImgError = () => {
    const url = imageUrl.value;
    if (!url) {
      return;
    }
    if (retryCount.value >= maxRetries) {
      return;
    }

    retryCount.value += 1;
    const delay = baseDelayMs * Math.pow(2, retryCount.value - 1);

    clearRetryTimer();
    retryTimer = setTimeout(() => {
      setFreshSrc(url);
    }, delay);
  };

  const onImgLoad = () => {
    retryCount.value = 0;
    clearRetryTimer();
  };

  onBeforeUnmount(() => {
    clearRetryTimer();
  });

  const fallbackSrc = computed(
    () => 'https://placehold.co/350x180/EEEEEE/AAAAAA?text=No+image'
  );

  return {
    imgSrc,
    fallbackSrc,
    retryCount,
    onImgError,
    onImgLoad
  };
};
